import React, { useCallback, useEffect, useState } from "react";
import { useRootSelector } from "~redux/hooks";
import { isDrawEditingSelector } from "~redux/slices/eramStateSlice";
import type { CursorOverride } from "@poscon/shared-frontend";
import {
  eramCursorCenterMap,
  errorTone,
  CursorContextProvider as BaseCursorContextProvider,
  useCursorContext as useBaseCursorContext,
} from "@poscon/shared-frontend";
import { delTearOffCommandActiveSelector } from "~/redux/slices/eramTempStateSlice";

export const CursorContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [cursorOverride, setCursorOverride] = useState<CursorOverride | null>(null);
  const delTearOffCommandActive = useRootSelector(delTearOffCommandActiveSelector);
  const drawEditing = useRootSelector(isDrawEditingSelector);
  const cursorState = useRootSelector((state) => state.eram.cursor);

  useEffect(() => {
    let cursorName = `ERAMCursor${cursorState.CURSOR_SIZE}`;
    if (cursorOverride) {
      cursorName = `ERAM${cursorOverride}`;
    } else if (delTearOffCommandActive) {
      cursorName = "ERAMDeletionCursor";
    } else if (drawEditing) {
      cursorName = "ERAMDrawCursor";
    }
    const cursorCenter = eramCursorCenterMap[cursorName]?.join(" ") ?? "";
    document.body.style.cursor = `url(/cursors/${cursorName}.png) ${cursorCenter}, auto`;
    errorTone.volume = cursorState.CURSOR_VOLUME / 5;
  }, [cursorState, cursorOverride, delTearOffCommandActive, drawEditing]);

  const onCounterError = useCallback((err: "MIN_VALUE" | "MAX_VALUE") => {
    void errorTone.tryPlay();
    if (err === "MIN_VALUE") {
      setCursorOverride("InvalidSelectionE");
    } else {
      setCursorOverride("InvalidSelectionP");
    }
  }, []);

  const onTBHError = useCallback(() => {
    void errorTone.tryPlay();
    setCursorOverride("InvalidSelectionI");
  }, []);

  useEffect(() => {
    let timeout: ReturnType<typeof window.setTimeout> | null = null;
    if (cursorOverride) {
      timeout = setTimeout(() => {
        setCursorOverride(null);
      }, 1000);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [cursorOverride]);

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <BaseCursorContextProvider value={{ setCursorOverride, onCounterError, onTBHError }}>
      {children}
    </BaseCursorContextProvider>
  );
};

export const useCursorContext = useBaseCursorContext;
